import { memo, useMemo } from 'react';

import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';

import { CloseIcon, colors } from '../../../assets';
import { getFileType } from '../../../utils';
import { FilePreview } from '../../attachmentCard/FilePreview';
import { useStyles } from './AttachmentItem.styles';
import { AttachmentItemProps } from './AttachmentItem.types';

export const AttachmentItem = memo(
  ({
    name,
    description,
    fileUrl,
    permissions,
    classes,
    typeOfAttachment,
    disabled,
    onPreview,
    onDelete,
    previewSize = {
      width: 30,
      height: 35,
    },
  }: AttachmentItemProps) => {
    const styles = useStyles({ previewSize });

    const descriptionContent = useMemo(() => {
      if (!typeOfAttachment && !description) {
        return;
      }

      return (
        <>
          {typeOfAttachment && <b>{typeOfAttachment}</b>}
          {typeOfAttachment && description && ' - '}
          {description && description}
        </>
      );
    }, [typeOfAttachment, description]);

    const fileType = useMemo(() => getFileType(name), [name]);

    const shouldShowPreview = useMemo(() => {
      return fileUrl && (fileType === 'image' || fileType === 'pdf' || fileType === 'video');
    }, [fileUrl, fileType]);

    return (
      <div className={styles.root}>
        <div className={clsx(styles.content, classes?.content, !!onPreview && 'cursorPointer')} onClick={onPreview}>
          <div className={clsx(styles.fileIconWrapper, shouldShowPreview && styles.previewContainer)}>
            <FilePreview
              fileName={name}
              fileUrl={fileUrl}
              width={previewSize.width}
              height={previewSize.height}
              previewClassName={styles.preview}
              iconClassName={styles.fileIcon}
              pdfThumbnailClassName={styles.pdfThumbnail}
              pdfLoadingClassName={styles.pdfPreviewLoading}
            />
          </div>

          <div className="overflowHidden">
            <Typography variant="subtitle1" className={clsx(styles.name, classes?.name)}>
              {name}
            </Typography>

            {descriptionContent && (
              <Typography variant="body1" className={clsx(styles.description, classes?.description)}>
                {descriptionContent}
              </Typography>
            )}
          </div>
        </div>

        {!permissions?.hideDelete && (
          <IconButton disabled={disabled} className={styles.removeIconButton} onClick={onDelete}>
            <CloseIcon color={disabled ? colors.grey40 : colors.grey80} fontSize={12} />
          </IconButton>
        )}
      </div>
    );
  },
);
